import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  signal,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { Subscription } from 'rxjs';

import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  UntypedFormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import {
  DataStoreExport,
  FailedAddress,
  Particularity,
  Product,
  UserInRide,
} from '../../../../../interfaces';
import { MatSnackBar } from '@angular/material/snack-bar';
import { query, collection, doc, updateDoc, onSnapshot, orderBy, getDoc } from 'firebase/firestore';
import moment from 'moment';
import { PlanType } from 'src/app/enums';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';
import {
  hasMatchingAddressCoordinates,
  resolveLocationCoordinates,
} from '../../../../helpers/ride-coordinate-resolver';

import { MatSelect, MatOption, MatSelectTrigger } from '@angular/material/select';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatAutocomplete,
} from '@angular/material/autocomplete';
import { dataStore, db } from 'src/app/app.component';
import { CustomValidators } from 'angular-custom-validators';
import { checkUserDoubleBooking } from 'src/app/planner/helpers/ride-helper';
import { valueToDate } from 'src/app/globals';
import { OrsAddressAutocompleteComponent } from 'src/app/components/ors-address-autocomplete/ors-address-autocomplete.component';
import { AddressValidationService } from 'src/app/services/address-validation.service';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { OrsAddressAutocompleteComponent as OrsAddressAutocompleteComponent_1 } from '../../../../../components/ors-address-autocomplete/ors-address-autocomplete.component';
import { MatIconButton, MatButton } from '@angular/material/button';
export interface DialogData {
  user: any;
  users: Array<object>;
  dialogStart: any;
  dialogEnd: any;
  particularities: Array<Particularity>;
  rideId?: any;
  rideDates?: { start: any; end: any };
  planType?: PlanType;
  excludeRideId?: string;
  forceRideStartEnd?: boolean;
}

export interface Address {
  id?: string;
  name: string;
  address: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  coordinatesFor?: string;
}

@Component({
  selector: 'app-user-ride',
  templateUrl: './user-ride.component.html',
  styleUrls: ['./user-ride.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatTooltip,
    MatIcon,
    NgClass,
    MatSelect,
    OrsAddressAutocompleteComponent_1,
    MatError,
    MatSelectTrigger,
    MatIconButton,
    MatButton,
  ],
})
export class UserRideComponent implements OnInit, OnDestroy {
  unsubscribes: (() => void)[] = [];
  subscriptions = new Subscription();
  @Input() data: DialogData;
  @Output() added = new EventEmitter<UserInRide>();
  @Output() canceled = new EventEmitter<void>();

  @ViewChild('fromTypeSelect', { static: false }) fromType: MatSelect;
  @ViewChild('toTypeSelect', { static: false }) toType: MatSelect;
  @ViewChild('fromStandardAddressInput', { static: false })
  FromStandardInput: ElementRef;
  @ViewChild('toStandardAddressInput', { static: false })
  ToStandardInput: ElementRef;
  @ViewChild('fromLatestAddressInput', { static: false })
  FromLatestInput: MatSelect;
  @ViewChild('toLatestAddressInput', { static: false }) ToLatestInput: MatSelect;
  @ViewChild('orsFromInput', { static: false }) OrsFromInput: OrsAddressAutocompleteComponent;
  @ViewChild('orsToInput', { static: false }) OrsToInput: OrsAddressAutocompleteComponent;
  ORS_FROM_INPUT = 'ORS_FROM_INPUT';
  ORS_TO_INPUT = 'ORS_TO_INPUT';
  fromHidden = signal(false);
  toHidden = signal(false);
  standardFromHidden = signal(true);
  standardToHidden = signal(true);
  recentFromHidden = signal(true);
  recentToHidden = signal(true);
  lastClickedInput: string;
  loading = signal(false);

  dataStore: DataStoreExport = dataStore;
  organisationId = this.dataStore.selectedOrganisation.id;
  PlanType = PlanType;

  nothingFound = signal(false);

  userForm: UntypedFormGroup;

  userSelected = signal(true);
  planningComments = signal(false);
  driverComments = signal(false);
  user = signal<UserInRide | null>(null);
  isEditing = signal(false);

  rideTimeForm: UntypedFormGroup;
  startHour: number;
  endHour: number;
  startMinutes: number;
  endMinutes: number;
  minutes = signal<{ val: number; text: string; invalid?: boolean }[]>([]);
  hours = signal<number[]>([]);
  invalidTimeCombination = signal(false);
  maxRideMinutesExceeded = signal(false);

  locationTypes = [
    { val: 'home', name: 'Thuisadres gebruiken' },
    { val: 'custom', name: 'Adres handmatig invullen' },
    { val: 'addressFromDb', name: 'Standaardlocatie gebruiken' },
    { val: 'recent', name: 'Laatste adressen gebruiken' },
  ];
  recentDestinations = signal<string[]>([]);
  addresses: Address[] = [];
  addressesFrom = signal<Address[]>([]);
  addressesTo = signal<Address[]>([]);
  predictionFrom: string[] = [];
  predictionTo: string[] = [];

  products = signal<Product[]>([]);
  showRideProduct = signal(false);

  doubleBookingActivated: boolean = false;
  userAlreadyBooked: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private addressValidationService: AddressValidationService,
  ) {}

  ngOnDestroy() {
    this.unsubscribes.forEach((unsubscribe) => unsubscribe());
    this.subscriptions.unsubscribe();
  }

  async ngOnInit() {
    const userInRide = (this.data.users as any[]).find((u) => u.id === this.data.user?.id);
    if (userInRide) {
      this.isEditing.set(true);
    }

    const userWithAddress = {
      ...this.data.user,
      title: this.data.user.title ?? this.data.user.name,
    };
    if (!userWithAddress.address && userWithAddress.streetName) {
      userWithAddress.address = this.formatUserAddress(userWithAddress);
    }
    this.user.set(userWithAddress as UserInRide);
    this.userSelected.set(true);
    console.log('User selected', this.user());
    let fromType = this.data?.user?.fromType ?? 'home';
    let toType = this.data?.user?.toType ?? 'custom';
    this.userForm = this.fb.group({
      from: [this.data?.user?.from, Validators.required],
      fromType: [fromType, Validators.required],
      to: [this.data?.user?.to, Validators.required],
      toType: [toType, Validators.required],
      remarks: [this.data?.user?.remarks],
      commentsForDriver: [this.data?.user?.commentsForDriver],
      rideProduct: [this.data?.user?.rideProduct ? this.data.user.rideProduct.id : 'none'],
      fromSearch: [],
      toSearch: [],
      particularityIds: [this.data?.user?.particularityIds?.slice() ?? []],
      userPickupTime: [valueToDate(this.data?.user?.userPickupTime)],
      userDropoffTime: [valueToDate(this.data?.user?.userDropoffTime)],
    });

    if (dataStore.paidFunctionalities?.usesAutomaticPlanning) {
      this.userForm.addControl(
        'vehicleEntryDuration',
        new UntypedFormControl(
          this.user()?.vehicleEntryDuration ?? dataStore.generalSettings.vehicleEntryDuration ?? 0,
          [Validators.max(15), CustomValidators.numberInput(false, false, 0)],
        ),
      );
      this.userForm.addControl(
        'vehicleExitDuration',
        new UntypedFormControl(
          this.user()?.vehicleExitDuration ?? dataStore.generalSettings.vehicleExitDuration ?? 0,
          [Validators.max(15), CustomValidators.numberInput(false, false, 0)],
        ),
      );
    }
    if (this.data?.user && Object.keys(this.data.user).length > 0) {
      await this.setDefaultLocations();
    }

    let startTime = valueToDate(this.data?.user?.userPickupTime ?? this.data?.rideDates?.start);
    let endTime = valueToDate(this.data?.user?.userDropoffTime ?? this.data?.rideDates?.end);

    if (this.data.forceRideStartEnd) {
      startTime = valueToDate(this.data.rideDates.start);
      endTime = valueToDate(this.data.rideDates.end);
    }

    const validStartTime: boolean =
      moment(startTime).isSameOrAfter(moment(this.data?.rideDates?.start)) &&
      moment(startTime).isSameOrBefore(moment(this.data?.rideDates?.end));
    const validEndTime: boolean =
      moment(endTime).isSameOrBefore(moment(this.data?.rideDates?.end)) &&
      moment(endTime).isSameOrAfter(moment(this.data?.rideDates?.start));
    this.rideTimeForm = this.fb.group({
      startHour: [startTime ? startTime.getHours() : null, Validators.required],
      startMinute: [startTime ? startTime.getMinutes() : null, Validators.required],
      endHour: [endTime ? endTime.getHours() : null, Validators.required],
      endMinute: [endTime ? endTime.getMinutes() : null, Validators.required],
    });
    if (!this.data.forceRideStartEnd && (!validStartTime || !validEndTime)) {
      this.rideTimeForm.markAllAsTouched();
    }

    this.startHour =
      new Date(this.data?.rideDates?.start).getHours() ??
      dataStore.generalSettings.daypartSettings.startHour ??
      0;
    this.endHour =
      new Date(this.data?.rideDates?.end).getHours() ??
      dataStore.generalSettings.daypartSettings.endHour ??
      24;
    this.startMinutes = new Date(this.data?.rideDates?.start).getMinutes() ?? 0;
    this.endMinutes = new Date(this.data?.rideDates?.end).getMinutes() ?? 0;

    for (let i = this.startHour; i <= this.endHour; i++) {
      this.hours.update((hours) => [...hours, i]);
    }
    const minuteStep = 5;
    for (let i = 0; i < 60; i += minuteStep) {
      this.minutes.update((minutes) => [
        ...minutes,
        {
          val: i,
          text: `0${i}`.slice(-2),
        },
      ]);
    }

    this.validUserTimes(this.rideTimeForm.value);
    this.subscriptions.add(
      this.rideTimeForm.valueChanges.subscribe((val) => {
        const userTimes = this.validUserTimes(val);
        if (userTimes && !this.invalidTimeCombination() && !this.maxRideMinutesExceeded()) {
          this.userForm.patchValue({
            userPickupTime: new Date(valueToDate(userTimes.baseStartDate)),
            userDropoffTime: new Date(valueToDate(userTimes.baseEndDate)),
          });
        }
      }),
    );

    // if (dataStore.generalSettings.orsApiKey) {
    //   this.setupSuggestionsListener('from', 'predictionFrom');
    //   this.setupSuggestionsListener('to', 'predictionTo');
    // }
    this.setupStandardLocationListener('fromSearch');
    this.setupStandardLocationListener('toSearch');

    this.unsubscribes.push(
      onSnapshot(
        query(collection(db, `organisations/${this.organisationId}/products`)),
        (snapshot) => {
          this.products.set(
            snapshot.docs.map((doc) => {
              return { id: doc.id, ...doc.data() } as Product;
            }),
          );
          this.products().sort((a: Product, b: Product) => {
            return a.name.localeCompare(b.name, undefined, {
              numeric: true,
              sensitivity: 'base',
            });
          });
        },
      ),
    );

    this.unsubscribes.push(
      onSnapshot(
        query(
          collection(db, `organisations/${this.organisationId}/addresses`),
          orderBy('name', 'asc'),
        ),
        (snapshot) => {
          this.addresses = snapshot.docs.map((doc) => {
            return { id: doc.id, ...doc.data() } as Address;
          });
          this.addressesTo.set([...this.addresses]);
          this.addressesFrom.set([...this.addresses]);

          this.setAddressTypeVisibility('from', this.data?.user?.fromType);
          this.setAddressTypeVisibility('to', this.data?.user?.toType);

          const addressFrom = this.data?.user?.['from'];
          if (addressFrom) {
            const selectedAddressFrom = this.addresses.find((a) => a.id === addressFrom.id);
            if (selectedAddressFrom) {
              this.userForm.patchValue({
                ['from']: selectedAddressFrom.id,
                ['from' + 'Search']: selectedAddressFrom.address,
              });
            }
          }
          const addressTo = this.data?.user?.['to'];
          if (addressTo) {
            const selectedAddressTo = this.addresses.find((a) => a.id === addressTo.id);
            if (selectedAddressTo) {
              this.userForm.patchValue({
                ['to']: selectedAddressTo.id,
                ['to' + 'Search']: selectedAddressTo.address,
              });
            }
          }
        },
      ),
    );

    if (this.user()) {
      if (!this.user().recentDestinations) {
        this.recentDestinations.set(
          (
            await getDoc(doc(db, `organisations/${this.organisationId}/users/${this.user().id}`))
          ).data()?.recentDestinations ?? [],
        );
      } else {
        this.recentDestinations.set(this.user().recentDestinations);
      }

      if (
        this.user().fromType === 'addressFromDb' &&
        !this.recentDestinations().includes(this.user().from)
      ) {
        this.recentDestinations.update((dest) => [...dest, this.user().from]);
      }
      if (
        this.user().toType === 'addressFromDb' &&
        !this.recentDestinations().includes(this.user().to)
      ) {
        this.recentDestinations.update((dest) => [...dest, this.user().to]);
      }
    }
    this.showRideProduct.set(dataStore.generalSettings.showRideProduct);
    this.doubleBookingActivated = dataStore.generalSettings.doubleBookingActivated;
    this.planningComments.set(dataStore.generalSettings.pCommentsToggle ? true : false);
    this.driverComments.set(dataStore.generalSettings.dCommentsToggle ? true : false);
  }

  validUserTimes(value) {
    this.invalidTimeCombination.set(false);
    this.maxRideMinutesExceeded.set(false);
    let invalidMinutes = false;
    if (value.startHour !== null && value.endHour !== null) {
      const baseStartDate = new Date(this.data?.rideDates?.start) ?? new Date();
      const baseEndDate = new Date(this.data?.rideDates?.end) ?? new Date();
      baseStartDate.setHours(value.startHour, value.startMinute);
      baseEndDate.setHours(value.endHour, value.endMinute);
      if (baseStartDate < this.data?.rideDates?.start) {
        invalidMinutes = true;
        this.rideTimeForm.controls.startMinute.setValue(null, { emitEvent: false });
      }
      if (baseEndDate > this.data?.rideDates?.end) {
        invalidMinutes = true;
        this.rideTimeForm.controls.endMinute.setValue(null, { emitEvent: false });
      }
      if (value.startMinute === null || value.endMinute === null) {
        invalidMinutes = true;
      }
      if (invalidMinutes) {
        this.rideTimeForm.markAllAsTouched();
        return;
      }

      if (baseStartDate >= baseEndDate) {
        this.invalidTimeCombination.set(true);
      } else if (
        dataStore.generalSettings.maxPassengerRideMinutes &&
        moment(baseEndDate).diff(moment(baseStartDate), 'minutes') >
          (dataStore.generalSettings.maxPassengerRideMinutes ?? 45)
      ) {
        this.maxRideMinutesExceeded.set(true);
      }
      return { baseStartDate, baseEndDate };
    }
  }

  // !DO NOT DELETE! code using Google Places API to find suggestions
  // setupSuggestionsListener(controlName: string, predictionProperty: string) {
  //   this.userForm.controls[controlName].valueChanges
  //     .pipe(
  //       debounceTime(1000),
  //       switchMap((addressString: string) => {
  //         if (this.userForm.value[controlName + 'Type'] !== 'custom' || !addressString) {
  //           return of([]);
  //         }
  //         const url = `https://api.openrouteservice.org/geocode/autocomplete?api_key=${dataStore.generalSettings.orsApiKey}&text=${addressString}&boundary.country=NL`;

  //         return this.http.get<any>(url).pipe(
  //           catchError((err) => {
  //             console.error('ORS API Error:', err);
  //             return of([]);
  //           }),
  //           map((response) => {
  //             return response?.features?.map((feature: any) => feature.properties) || [];
  //           }),
  //         );
  //       }),
  //     )
  //     .subscribe((suggestions: any[]) => {
  //       const suggestionsArray: string[] = [];
  //       for (const suggestion of suggestions) {
  //         if (suggestion.layer === 'venue') {
  //           const addressString = `${suggestion.name ?? ''} ${suggestion.street ?? ''} ${
  //             suggestion.housenumber ?? ''
  //           }, ${suggestion.postalcode ?? ''} ${suggestion.locality}`;
  //           if (suggestionsArray.indexOf(addressString) === -1) {
  //             suggestionsArray.push(addressString);
  //           }
  //         } else {
  //           const addressString = `${suggestion.street ?? suggestion.name} ${
  //             suggestion.housenumber ?? ''
  //           }, ${suggestion.postalcode ?? ''} ${suggestion.locality}`;
  //           if (suggestionsArray.indexOf(addressString) === -1) {
  //             suggestionsArray.push(addressString);
  //           }
  //         }
  //       }
  //       if (predictionProperty === 'predictionFrom') {
  //         this.predictionFrom = suggestionsArray;
  //       } else if (predictionProperty === 'predictionTo') {
  //         this.predictionTo = suggestionsArray;
  //       }
  //     });

  // Needs refactoring if/when we want to use it again, as ngZone is no longer used.
  // this.userForm.controls['from'].valueChanges
  //   .pipe(debounceTime(1000))
  //   .subscribe((addressString) => {
  //     console.log('addressString from', addressString);
  //     console.log(
  //       'this.userForm.value.fromType',
  //       this.userForm.value.fromType.address
  //     );

  //     if (
  //       this.userForm.value.fromType === 'custom' &&
  //       addressString &&
  //       addressString.length !== 0
  //     ) {
  //       console.log('update suggestions');

  //       const request: google.maps.places.AutocompleteRequest = {
  //         input: addressString,
  //         includedRegionCodes: ['nl'],
  //         language: 'nl',
  //         region: 'nl',
  //         sessionToken: placeSessionToken,
  //       };
  //       AutocompleteSuggestion.fetchAutocompleteSuggestions(request).then(
  //         (result) => {
  //           console.log('addressSuggestions:', result);
  //           const array = [];
  //           if (result.suggestions) {
  //             result.suggestions.forEach(async (address) => {
  //               const place = new Place({
  //                 id: address.placePrediction.placeId,
  //                 requestedLanguage: 'nl',
  //                 requestedRegion: 'nl',
  //               });
  //               const placeDetails = await place.fetchFields({
  //                 fields: ['displayName', 'formattedAddress', 'location'],
  //               });
  //               array.push(placeDetails.place.formattedAddress);
  //             });
  //           }
  //           this.ngZone.run(() => {
  //             this.predictionFrom.next(array);
  //           });
  //         }
  //       );
  //     } else {
  //       return this.ngZone.run(() => {
  //         this.predictionFrom.next([]);
  //       });
  //     }
  //   });
  // this.userForm.controls['to'].valueChanges
  //   .pipe(debounceTime(1000))
  //   .subscribe((addressString) => {
  //     console.log('addressString to', addressString);
  //     console.log('this.userForm.value.toType', this.userForm.value.toType);
  //     const array = [];
  //     if (
  //       this.userForm.value.toType === 'custom' &&
  //       addressString &&
  //       addressString.length !== 0
  //     ) {
  //       console.log('update suggestions');
  //       const request: google.maps.places.AutocompleteRequest = {
  //         input: addressString,
  //         includedRegionCodes: ['nl'],
  //         // locationBias: 'IP_BIAS',
  //         language: 'nl',
  //         region: 'nl',
  //         sessionToken: placeSessionToken,
  //       };
  //       AutocompleteSuggestion.fetchAutocompleteSuggestions(request).then(
  //         (result) => {
  //           console.log('addressSuggestions:', result);
  //           const array = [];
  //           if (result.suggestions) {
  //             result.suggestions.forEach(async (address) => {
  //               const place = new Place({
  //                 id: address.placePrediction.placeId,
  //                 requestedLanguage: 'nl',
  //                 requestedRegion: 'nl',
  //               });
  //               const placeDetails = await place.fetchFields({
  //                 fields: ['displayName', 'formattedAddress', 'location'],
  //               });
  //               array.push(placeDetails.place.formattedAddress);
  //             });
  //           }
  //           this.predictionTo.next(array);
  //           this.ngZone.run(() => {
  //             this.predictionFrom.next(array);
  //           });
  //         }
  //       );
  //     } else {
  //       return this.ngZone.run(() => {
  //         this.predictionFrom.next([]);
  //       });
  //     }
  //   });
  // }

  setupStandardLocationListener(type: string) {
    this.subscriptions.add(
      this.userForm.controls[type].valueChanges.subscribe((value) => {
        this.nothingFound.set(false);
        if (type === 'toSearch') {
          this.addressesTo.set(this.filterStandardAddress(value));
          this.nothingFound.set(this.addressesTo().length === 0);
        } else if (type === 'fromSearch') {
          this.addressesFrom.set(this.filterStandardAddress(value));
          this.nothingFound.set(this.addressesFrom().length === 0);
        }
      }),
    );
  }

  onInputTypeChange(type, ev) {
    this.userForm.controls[type].enable();
    const patchObj = {};
    if (ev.value === 'custom') {
      patchObj[type] = '';
    } else if (ev.value === 'home') {
      const homeAddress = this.user()?.['address'] ?? this.formatUserAddress(this.user());
      if (homeAddress) {
        patchObj[type] = homeAddress;
        this.userForm.controls[type].disable();
      } else {
        patchObj[type + 'Type'] = 'custom';
        this.snackBar.open('Er is geen thuisadres ingevuld. Vul het adres handmatig in.', 'X', {
          duration: 10000,
        });
      }
    }
    this.userForm.patchValue(patchObj);

    this.setAddressTypeVisibility(type, ev.value);
    if (ev.value === 'addressFromDb') {
      this.lastClickedInput = type === 'from' ? 'fromStandard' : 'toStandard';
    } else if (ev.value === 'recent') {
      this.lastClickedInput = type === 'from' ? 'fromLatest' : 'toLatest';
    } else {
      this.lastClickedInput = type;
    }
  }

  deleteRecentDestination(destination: string) {
    const dialogRef = this.dialog.open(WarningDialog, {
      width: '600px',
      data: {
        dialogType: 'deleteRecentDestination',
        optionalData: {
          addressName: destination,
          deleteMode: true,
        },
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.recentDestinations.update((dest) => {
          const index = dest.indexOf(destination);
          if (index !== -1) {
            dest.splice(index, 1);
          }
          return [...dest];
        });
        await updateDoc(doc(db, `organisations/${this.organisationId}/users/${this.user().id}`), {
          recentDestinations: this.recentDestinations(),
        });
      }
    });
  }

  async save() {
    this.loading.set(true);
    const rideDates = {
      start: moment(
        this.data.rideDates.start.toDate
          ? this.data.rideDates.start.toDate()
          : this.data.rideDates.start,
      ),
      end: moment(
        this.data.rideDates.end.toDate ? this.data.rideDates.end.toDate() : this.data.rideDates.end,
      ),
    };
    const checkUserObj = {
      ...this.user(),
      userDropoffTime: this.userForm.value.userDropoffTime,
      userPickupTime: this.userForm.value.userPickupTime,
    };
    this.userAlreadyBooked = await checkUserDoubleBooking(
      checkUserObj,
      rideDates,
      this.data.rideId,
      this.organisationId,
      this.data.excludeRideId,
    );

    if (dataStore.paidFunctionalities?.usesAutomaticPlanning) {
      // Get the actual address values to validate
      const fromValue = this.userForm.get('from').value;
      const toValue = this.userForm.get('to').value;
      const fromType = this.userForm.get('fromType').value;
      const toType = this.userForm.get('toType').value;

      // Convert IDs to actual address strings if needed
      let fromAddressToValidate: string;
      let toAddressToValidate: string;

      if (fromType === 'addressFromDb' || fromType === 'recent') {
        const addressObj = this.addresses.find((addr) => addr.id === fromValue);
        fromAddressToValidate = addressObj ? addressObj.address : fromValue;
      } else if (fromType === 'home') {
        fromAddressToValidate = this.user()?.['address'];
      } else {
        fromAddressToValidate = fromValue;
      }

      if (toType === 'addressFromDb' || toType === 'recent') {
        const addressObj = this.addresses.find((addr) => addr.id === toValue);
        toAddressToValidate = addressObj ? addressObj.address : toValue;
      } else if (toType === 'home') {
        toAddressToValidate = this.user()?.['address'];
      } else {
        toAddressToValidate = toValue;
      }

      const selectedFromAddress =
        fromType === 'addressFromDb'
          ? this.addresses.find((address) => address.id === fromValue)
          : undefined;
      const selectedToAddress =
        toType === 'addressFromDb'
          ? this.addresses.find((address) => address.id === toValue)
          : undefined;
      const selectedFromCoordinatesMatch = hasMatchingAddressCoordinates(
        selectedFromAddress,
        (address) => this.addressValidationService.normalizeAddressForComparison(address),
      );
      const selectedToCoordinatesMatch = hasMatchingAddressCoordinates(
        selectedToAddress,
        (address) => this.addressValidationService.normalizeAddressForComparison(address),
      );
      const hasMatchingUserCoordinates = this.hasMatchingUserCoordinates();
      const addressFromResult = await resolveLocationCoordinates({
        locationType: fromType,
        directCoordinates: selectedFromCoordinatesMatch
          ? selectedFromAddress.coordinates
          : undefined,
        homeCoordinates: this.user()?.addressCoordinates,
        hasMatchingHomeCoordinates: hasMatchingUserCoordinates,
        addressToValidate: fromAddressToValidate,
        sourceLabel: 'user-ride:from',
        validateAddress: (address) => this.addressValidationService.validateAddress(address),
      });
      const addressToResult = await resolveLocationCoordinates({
        locationType: toType,
        directCoordinates: selectedToCoordinatesMatch ? selectedToAddress.coordinates : undefined,
        homeCoordinates: this.user()?.addressCoordinates,
        hasMatchingHomeCoordinates: hasMatchingUserCoordinates,
        addressToValidate: toAddressToValidate,
        sourceLabel: 'user-ride:to',
        validateAddress: (address) => this.addressValidationService.validateAddress(address),
      });

      if (!addressFromResult.isValid || !addressToResult.isValid) {
        const failedAddresses: FailedAddress[] = [];
        if (!addressFromResult.isValid) {
          failedAddresses.push({
            name: 'Ophaaladres',
            address: fromAddressToValidate,
            error: 'Dit adres is niet geldig',
          });
        }
        if (!addressToResult.isValid) {
          failedAddresses.push({
            name: 'Afzetadres',
            address: toAddressToValidate,
            error: 'Dit adres is niet geldig',
          });
        }

        this.dialog.open(WarningDialog, {
          width: '600px',
          data: {
            dialogType: 'addressNotValid',
            optionalData: {
              deleteMode: false,
              failedAddresses: failedAddresses,
            },
          },
        });
        this.loading.set(false);
        return;
      }

      await this.cacheStandardLocationCoordinates(
        fromType,
        fromValue,
        addressFromResult.coordinates,
      );
      await this.cacheStandardLocationCoordinates(toType, toValue, addressToResult.coordinates);
      await this.cacheUserAddressCoordinates(fromType, addressFromResult.coordinates);
      await this.cacheUserAddressCoordinates(toType, addressToResult.coordinates);
    }

    if (this.userAlreadyBooked && !this.doubleBookingActivated) {
      const dialogRef = this.dialog.open(WarningDialog, {
        width: '600px',
        data: { dialogType: 'userInRide' },
      });
      dialogRef.afterClosed().subscribe(async (result) => {
        if (result) {
          this.addUserToRide();
        }
        this.loading.set(false);
      });
    } else {
      this.loading.set(false);
      this.addUserToRide();
    }
  }

  addUserToRide() {
    const user = { ...this.user() };
    user['fromType'] = this.userForm.value.fromType;
    if (user['fromType'] === 'home') {
      user['from'] = user['address'];
    } else {
      const selectFromAddress = this.addresses.find((address) => {
        return address['id'] === this.userForm.value.from;
      });
      user['from'] = selectFromAddress ?? this.userForm.value.from;
    }

    user['particularityIds'] = this.userForm.value.particularityIds;

    user['toType'] = this.userForm.value.toType;
    if (user['toType'] === 'home') {
      user['to'] = user['address'];
    } else {
      const selectToAddress = this.addresses.find((address) => {
        return address['id'] === this.userForm.value.to;
      });
      user['to'] = selectToAddress ?? this.userForm.value.to;
    }

    user['userPickupTime'] = new Date(
      valueToDate(this.userForm.value.userPickupTime ?? this.data?.rideDates?.start),
    );
    user['userDropoffTime'] = new Date(
      valueToDate(this.userForm.value.userDropoffTime ?? this.data?.rideDates?.end),
    );

    user['rideProduct'] = this.products().find((product) => {
      return product['id'] === this.userForm.value.rideProduct;
    });
    if (!user['rideProduct']) {
      delete user['rideProduct'];
    }

    user['commentsForDriver'] = this.userForm.value.commentsForDriver ?? '';
    user['remarks'] = this.userForm.value.remarks ?? '';
    user['phoneNumber'] = user.phoneNumber ?? user.phone ?? '';

    if (dataStore.paidFunctionalities?.usesAutomaticPlanning) {
      user['vehicleEntryDuration'] = Number(this.userForm.value.vehicleEntryDuration);
      user['vehicleExitDuration'] = Number(this.userForm.value.vehicleExitDuration);
    }

    console.log('user added to ride', user);
    this.added.emit(user as UserInRide);
  }

  private async cacheStandardLocationCoordinates(
    addressType: string,
    addressId: string,
    coordinates?: { latitude: number; longitude: number },
  ): Promise<void> {
    if (addressType !== 'addressFromDb' || !addressId || !coordinates) {
      return;
    }

    const standardLocation = this.addresses.find((address) => address.id === addressId);
    if (!standardLocation || standardLocation.coordinates) {
      return;
    }

    try {
      await updateDoc(doc(db, `organisations/${this.organisationId}/addresses/${addressId}`), {
        coordinates,
        coordinatesFor: this.addressValidationService.normalizeAddressForComparison(
          standardLocation.address,
        ),
      });
      console.info('[CoordinateResolver] Saved coordinates to standard location document', {
        sourceLabel: 'user-ride',
        organisationId: this.organisationId,
        addressId,
        address: standardLocation.address,
        coordinates,
      });
      standardLocation.coordinates = coordinates;
    } catch (error) {
      // Best-effort cache write; failures here should not abort adding the user to the ride.
      console.warn('[CoordinateResolver] Failed to cache standard location coordinates', error);
    }
  }

  private async cacheUserAddressCoordinates(
    addressType: string,
    coordinates?: { latitude: number; longitude: number },
  ): Promise<void> {
    const user = this.user();
    const userAddress = user?.['address'] ?? this.formatUserAddress(user);
    if (addressType !== 'home' || !user?.id || !coordinates || user.addressCoordinates) {
      return;
    }

    try {
      await updateDoc(doc(db, `organisations/${this.organisationId}/users/${user.id}`), {
        addressCoordinates: coordinates,
        addressCoordinatesFor: this.addressValidationService.normalizeAddressForComparison(
          userAddress ?? '',
        ),
      });
      console.info('[CoordinateResolver] Saved home coordinates to user document', {
        sourceLabel: 'user-ride',
        organisationId: this.organisationId,
        userId: user.id,
        address: userAddress ?? '',
        coordinates,
      });
    } catch (error) {
      // Best-effort cache write; failures here should not abort adding the user to the ride.
      console.warn('[CoordinateResolver] Failed to cache user home coordinates', error);
      return;
    }
    this.user.set({
      ...user,
      addressCoordinates: coordinates,
      addressCoordinatesFor: this.addressValidationService.normalizeAddressForComparison(
        userAddress ?? '',
      ),
    });
  }

  private hasMatchingUserCoordinates(): boolean {
    const user = this.user();
    const userAddress = user?.['address'] ?? this.formatUserAddress(user);
    if (!user?.addressCoordinates) {
      return false;
    }

    if (!user.addressCoordinatesFor) {
      return true;
    }

    return (
      user.addressCoordinatesFor ===
      this.addressValidationService.normalizeAddressForComparison(userAddress ?? '')
    );
  }

  cancel() {
    this.canceled.emit();
  }

  filterStandardAddress(searchValue: string) {
    if (!searchValue || searchValue === '') {
      return this.addresses;
    }
    const filterValue = searchValue.toLowerCase();
    return this.addresses.filter(
      (address) =>
        address.name.toLowerCase().includes(filterValue) ||
        address.address.toLowerCase().includes(filterValue),
    );
  }

  toggleParticularity(particularityId: string) {
    const activeParticularities: string[] = this.userForm.value.particularityIds;
    if (activeParticularities.indexOf(particularityId) !== -1) {
      const index = activeParticularities.indexOf(particularityId);
      activeParticularities.splice(index, 1);
    } else {
      activeParticularities.push(particularityId);
    }
    this.userForm.controls.particularityIds.setValue(activeParticularities);
  }

  standardAddressSelected(event: MatAutocompleteSelectedEvent, type: 'from' | 'to') {
    const selectedAddress = this.addresses.find((address) => {
      return address.id === event.option.value;
    });
    if (type === 'from') {
      this.userForm.controls.from.setValue(selectedAddress.id);
      this.userForm.controls.fromSearch.setValue(selectedAddress.address);
    }
    if (type === 'to') {
      this.userForm.controls.to.setValue(selectedAddress.id);
      this.userForm.controls.toSearch.setValue(selectedAddress.address);
    }
  }

  setAddressTypeVisibility(type: 'from' | 'to', value: string) {
    const hiddenProperty = type === 'from' ? 'fromHidden' : 'toHidden';
    const standardHiddenProperty = type === 'from' ? 'standardFromHidden' : 'standardToHidden';
    const recentHiddenProperty = type === 'from' ? 'recentFromHidden' : 'recentToHidden';
    if (value === 'addressFromDb') {
      this[hiddenProperty].set(true);
      this[standardHiddenProperty].set(false);
      this[recentHiddenProperty].set(true);
    } else if (value === 'recent') {
      this[hiddenProperty].set(true);
      this[standardHiddenProperty].set(true);
      this[recentHiddenProperty].set(false);
    } else {
      this[hiddenProperty].set(false);
      this[standardHiddenProperty].set(true);
      this[recentHiddenProperty].set(true);
    }
  }

  async setDefaultLocations() {
    if (this.user()) {
      const validAddress = this.user()?.['address'] ?? this.formatUserAddress(this.user());
      if (this.userForm.value.fromType === 'home') {
        if (validAddress) {
          this.userForm.controls.from.setValue(this.user()['address']);
          this.userForm.controls.from.disable();
        } else {
          this.userForm.controls.fromType.setValue('custom');
        }
      }
      if (this.userForm.value.toType === 'home') {
        if (validAddress) {
          this.userForm.controls.to.setValue(this.user()['address']);
          this.userForm.controls.to.disable();
        } else {
          this.userForm.controls.toType.setValue('custom');
        }
      }
    }
  }

  displayMinute(minute: number, type: 'start' | 'end') {
    if (type === 'end') {
      if (
        (this.rideTimeForm.value.endHour === this.endHour && minute > this.endMinutes) ||
        (this.rideTimeForm.value.endHour === this.startHour && minute < this.startMinutes)
      ) {
        return false;
      }
    } else if (type === 'start') {
      if (
        (this.rideTimeForm.value.startHour === this.startHour && minute < this.startMinutes) ||
        (this.rideTimeForm.value.startHour === this.endHour && minute > this.endMinutes)
      ) {
        return false;
      }
    }
    return true;
  }

  focusNextInputField(type, $event) {
    if (!$event) {
      // setTimeout is used to make sure that the element to select exists (or should exist).
      // Without this the element is often still in progress of being rendered and thus does not yet exist in the DOM.
      // setTimeout is never called before the rendering is done, thus ensuring that the expected element can exist in the DOM.
      setTimeout(() => {
        const inputType: string =
          type === 'from'
            ? this.userForm.controls.fromType.value
            : this.userForm.controls.toType.value;
        switch (inputType) {
          case 'custom':
            const customInput: OrsAddressAutocompleteComponent =
              type === 'from' ? this.OrsFromInput : this.OrsToInput;
            if (customInput) {
              // Focus is automatically handled by the component
            }
            break;
          case 'addressFromDb':
            const addressFromDbInput: ElementRef =
              type === 'from' ? this.FromStandardInput : this.ToStandardInput;
            if (addressFromDbInput) {
              addressFromDbInput.nativeElement.click();
            }
            break;
          case 'recent':
            const recentInput: MatSelect =
              type === 'from' ? this.FromLatestInput : this.ToLatestInput;
            if (recentInput) {
              recentInput.trigger.nativeElement.click();
            }
            break;
          case 'home':
            return;
        }
        if (inputType !== 'addressFromDb') {
          this.userForm.patchValue(type === 'from' ? { fromSearch: '' } : { toSearch: '' });
        }
      });
    }
  }

  formatUserAddress(user: any): string | undefined {
    if (!user || !user.streetName) {
      return undefined;
    }
    if (user.houseNumberAddition) {
      return `${user.streetName} ${user.houseNumber}-${user.houseNumberAddition}, ${user.postal} ${user.city}`;
    } else {
      return `${user.streetName} ${user.houseNumber}, ${user.postal} ${user.city}`;
    }
  }
}
